import {
  getElementDescendant,
  getFormattedFieldDisplayValue,
} from "../../helpers/utils";

export type RiskTone = "danger" | "muted" | "warning";

export const getDisplayValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    const wrapped = value as { units?: unknown; value?: unknown };
    const wrappedValue = wrapped.value;

    if (
      wrappedValue === undefined ||
      wrappedValue === null ||
      wrappedValue === ""
    ) {
      return "-";
    }

    return wrapped.units ? `${wrappedValue} ${wrapped.units}` : `${wrappedValue}`;
  }

  return `${value}`;
};

export const getNestedValue = (element: unknown, path: string[]) =>
  getElementDescendant(element, [...path]) as unknown;

export const getNestedDisplayValue = (element: unknown, path: string[]) =>
  getDisplayValue(getNestedValue(element, path));

export const getFormattedDisplayValue = (
  element: unknown,
  field: string,
  formatters: unknown,
) => getFormattedFieldDisplayValue(element, field, formatters);

export const getScientificDisplayValue = (
  element: unknown,
  field: string,
  decimals = 3,
) => {
  const valueObject = getNestedValue(
    element,
    field.endsWith(".value") ? field.split(".").slice(0, -1) : field.split("."),
  ) as { units?: unknown; value?: unknown } | undefined;
  const rawValue = valueObject?.value;

  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return "-";
  }

  const numericValue =
    typeof rawValue === "number" ? rawValue : Number.parseFloat(`${rawValue}`);

  if (!Number.isFinite(numericValue)) {
    return getDisplayValue(valueObject);
  }

  const formattedValue = numericValue.toExponential(decimals);

  return valueObject?.units
    ? `${formattedValue} ${getDisplayValue(valueObject.units)}`
    : formattedValue;
};

export const getRiskTone = (riskLevel: unknown): RiskTone => {
  const risk = getDisplayValue(riskLevel).toUpperCase();

  if (risk === "ALERT") return "danger";
  if (risk === "WARN" || risk === "WARNING") return "warning";

  return "muted";
};

export const getRiskLabel = (riskLevel: unknown) => {
  const risk = getDisplayValue(riskLevel).toUpperCase();

  if (risk === "ALERT") return "ALERT";
  if (risk === "WARN" || risk === "WARNING") return "WARNING";
  if (risk === "INFO") return "INFO";

  return getDisplayValue(riskLevel);
};

const parseNumericValue = (value: unknown) => {
  if (value && typeof value === "object" && "value" in value) {
    return parseNumericValue((value as { value?: unknown }).value);
  }

  if (value === undefined || value === null || value === "") {
    return null;
  }

  const numericValue = Number(`${value}`);

  return Number.isFinite(numericValue) ? numericValue : null;
};

export const getThresholdTone = (
  value: unknown,
  threshold: unknown,
  mode: "absolute" | "lower" | "upper" = "upper",
) => {
  const numericValue = parseNumericValue(value);
  const numericThreshold = parseNumericValue(threshold);

  if (numericValue === null || numericThreshold === null) {
    return "default" as const;
  }

  const isDanger =
    mode === "absolute"
      ? Math.abs(numericValue) <= Math.abs(numericThreshold)
      : mode === "lower"
        ? numericValue >= 0 && numericValue <= numericThreshold
        : numericValue >= numericThreshold;

  return isDanger ? ("danger" as const) : ("default" as const);
};

export const getNestedThresholdTone = (
  element: unknown,
  valuePath: string[],
  thresholdPath: string[],
  mode: "absolute" | "lower" | "upper" = "upper",
) =>
  getThresholdTone(
    getElementDescendant(element, valuePath),
    getElementDescendant(element, thresholdPath),
    mode,
  );

export interface RiskThresholdSet {
  timeTCA?: unknown;
  minDist?: unknown;
  radDist?: unknown;
  crossDist?: unknown;
  alongDist?: unknown;
  prob?: unknown;
}

export interface RiskLevelSet {
  warningLevel?: RiskThresholdSet;
  alertLevel?: RiskThresholdSet;
}

export interface ConjunctionRiskConfig {
  defRiskLevel?: RiskLevelSet;
}

export interface ConjunctionSummaryField {
  label: string;
  value: string;
  tone: "danger" | "muted" | "warning" | "default";
  strong?: boolean;
}

const getThresholds = (
  config?: ConjunctionRiskConfig,
  level: "warningLevel" | "alertLevel" = "warningLevel",
) => config?.defRiskLevel?.[level];

const getThresholdByMetric = (
  config: ConjunctionRiskConfig | undefined,
  metric: "timeTCA" | "minDist" | "radDist" | "crossDist" | "alongDist" | "prob",
  level: "warningLevel" | "alertLevel",
) => getThresholds(config, level)?.[metric];

export const getConjunctionMetricTone = (
  value: unknown,
  config: ConjunctionRiskConfig | undefined,
  metric: "timeTCA" | "minDist" | "radDist" | "crossDist" | "alongDist" | "prob",
) => {
  const warningThreshold = getThresholdByMetric(config, metric, "warningLevel");
  const alertThreshold = getThresholdByMetric(config, metric, "alertLevel");
  const mode =
    metric === "prob"
      ? "upper"
      : metric === "minDist"
        ? "lower"
        : "absolute";

  // Separation metrics are symmetric around 0, so +/- values share the same tone.
  const alertTone = getThresholdTone(value, alertThreshold, mode);
  if (alertTone === "danger") {
    return "danger" as const;
  }

  const warningTone = getThresholdTone(value, warningThreshold, mode);
  if (warningTone === "danger") {
    return "warning" as const;
  }

  return "default" as const;
};

export const getProbabilityFamilyTone = (
  value: unknown,
  config: ConjunctionRiskConfig | undefined,
) => getConjunctionMetricTone(value, config, "prob");

export const getTimeToTcaSeconds = (
  executionTime?: unknown,
  tca?: unknown,
) => {
  const start = new Date(getDisplayValue(executionTime));
  const end = new Date(getDisplayValue(tca));

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  return (end.getTime() - start.getTime()) / 1000;
};

export const getConjunctionSummaryFields = (
  conjunction: unknown,
  formatters: unknown,
  riskConfig?: ConjunctionRiskConfig,
): ConjunctionSummaryField[] => [
  {
    label: "MISS DIST.",
    value: getFormattedDisplayValue(conjunction, "missDistance.value", formatters),
    strong: true,
    tone: getConjunctionMetricTone(
      getNestedValue(conjunction, ["missDistance"]),
      riskConfig,
      "minDist",
    ),
  },
  {
    label: "RADIAL",
    value: getFormattedDisplayValue(conjunction, "radialDistance.value", formatters),
    strong: true,
    tone: getConjunctionMetricTone(
      getNestedValue(conjunction, ["radialDistance"]),
      riskConfig,
      "radDist",
    ),
  },
  {
    label: "ALONG",
    value: getFormattedDisplayValue(
      conjunction,
      "alongTrackDistance.value",
      formatters,
    ),
    strong: true,
    tone: getConjunctionMetricTone(
      getNestedValue(conjunction, ["alongTrackDistance"]),
      riskConfig,
      "alongDist",
    ),
  },
  {
    label: "CROSS",
    value: getFormattedDisplayValue(
      conjunction,
      "crossTrackDistance.value",
      formatters,
    ),
    strong: true,
    tone: getConjunctionMetricTone(
      getNestedValue(conjunction, ["crossTrackDistance"]),
      riskConfig,
      "crossDist",
    ),
  },
  {
    label: "POC",
    value: getFormattedDisplayValue(
      conjunction,
      "collisionProbability.value",
      formatters,
    ),
    strong: true,
    tone: getProbabilityFamilyTone(
      getNestedValue(conjunction, ["collisionProbability"]),
      riskConfig,
    ),
  },
  {
    label: "SPOC",
    value: getFormattedDisplayValue(
      conjunction,
      "scaledCollisionProbability.value",
      formatters,
    ),
    strong: true,
    tone: getProbabilityFamilyTone(
      getNestedValue(conjunction, ["collisionProbability"]),
      riskConfig,
    ),
  },
  {
    label: "MPOC",
    value: getFormattedDisplayValue(
      conjunction,
      "maximumCollisionProbability.value",
      formatters,
    ),
    strong: true,
    tone: getProbabilityFamilyTone(
      getNestedValue(conjunction, ["collisionProbability"]),
      riskConfig,
    ),
  },
];
